import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { FaEdit } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useCreateStoreMutation, useDeleteStoreMutation, useGetStoreListQuery, useUpdateStoreMutation } from "../../redux/api/storeApi/storeApi";
import { selectAuth, setActiveTab } from "../../redux/features/authSlice";
import { IAuthUser } from "../../redux/features/authSlice/authSlice.types";
import jwt_decode from "jwt-decode";
import CustomLoading from "../../components/CustomLoading";
import CustomModal from "../../components/CustomModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInput from "../../components/CustomInput";
import { useGetProfileQuery } from "../../redux/api/profileApi/profileApi";
import { AiOutlineDelete } from "react-icons/ai";

const StoreList = () => {
    const { token } = useSelector(selectAuth);
    const authData: IAuthUser = token ? jwt_decode(token) : null;
    const { data: profileData, isLoading: profileLoading } = useGetProfileQuery(authData?.id);
    const permissions: string[] = profileData?.permissions ? JSON.parse(profileData?.permissions) : [];
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const [filterText, setFilterText] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [storeInfo, setStoreInfo] = useState(null);
    const [storeId, setStoreId] = useState(null);
    const { data: storeList, isLoading: storeListLoading } = useGetStoreListQuery(
        authData?.id
    );
    const [createStore, createResult] = useCreateStoreMutation();
    const [updateStore, updateResult] = useUpdateStoreMutation();
    const [storeStatus, setStoreStatus] = useState('Active');
    const [deleteStore, deleteResult] = useDeleteStoreMutation();

    const columns = [
        {
            name: "ID",
            selector: (row) => row?.id,
            sortable: true,
        },

        {
            name: "Store Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Pickup Address",
            selector: (row) => row.pickup_address,
            sortable: true,
        },
        {
            name: "Return Address",
            selector: (row) => row.return_address,
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) => (
                <div
                    className={`py-1 w-20 text-center rounded ${row.status === "Active"
                        ? "bg-success/10 text-success"
                        : "bg-attention/10 text-attention"
                        }`}
                >
                    {row.status}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Edit",
            cell: (row) => (
                <div
                    onClick={() => {
                        if (permissions?.includes("UpdateStore")) {
                            setStoreInfo(row);
                            setShowModal(true);
                        } else {
                            toast.info("You have not permitted!");
                        }
                    }}
                    className={`flex flex-row gap-2 items-center justify-center py-1 w-20 text-center rounded border bg-secondary/10 border-secondary text-secondary cursor-pointer transition-all hover:bg-secondary hover:animate-pulse hover:text-white`}
                >
                    <FaEdit /> Edit
                </div>
            ),
        },
        {
            name: "Delete",
            cell: (row) => (
                <div
                    onClick={() => {
                        if (permissions?.includes("RemoveStore")) {
                            setStoreId(row?.id);
                            setDeleteModal(true);
                        } else {
                            toast.info("You have not permitted!");
                        }
                    }}
                    className={`flex flex-row gap-2 items-center justify-center py-1 w-20 text-center rounded border bg-warning/10 border-warning text-warning cursor-pointer transition-all hover:bg-warning hover:animate-pulse hover:text-white`}
                >
                    <AiOutlineDelete /> Delete
                </div>
            ),
        },
    ];

    const filteredBranch = storeList
        ?.filter(
            (item) =>
                item.name && item.name.toLowerCase().includes(filterText.toLowerCase())
        )
        ?.reverse();

    const validationSchema = Yup.object().shape({
        storename: Yup.string().required("Store name is required"),
        pickupaddress: Yup.string().required("Pickup address is required"),
        returnaddress: Yup.string().required("Return address is required"),
    });

    const initialValues = {
        storename: "",
        pickupaddress: "",
        returnaddress: "",
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            const createData = {
                merchant_id: authData?.id,
                name: values.storename,
                pickup_address: values.pickupaddress,
                return_address: values.returnaddress,
            };
            const updateData = {
                name: values.storename,
                pickup_address: values.pickupaddress,
                return_address: values.returnaddress,
                status: storeStatus
            };
            try {
                if (storeInfo) {
                    await updateStore({ data: updateData, storeId }).unwrap();
                    toast.success("Store updated successfully!");
                } else {
                    await createStore(createData).unwrap();
                    toast.success("Store created successfully!");
                }
                resetForm();
                setShowModal(false);
            } catch (error) {
                console.log(error);
                toast.error(error?.data?.message || "Something went wrong!");
            }
        },
    });

    const {
        handleSubmit,
        values,
        touched,
        errors,
        setFieldValue,
        handleBlur,
        resetForm,
    } = formik;


    const handleDeleteStore = async () => {
        try {
            if (storeId) {
                await deleteStore(storeId).unwrap();
                toast.success("Store deleted successfully!");
            }
            setDeleteModal(false);
        } catch (error) {
            console.log(error);
            toast.error(error?.data?.message || "Something went wrong!");
        }
    }

    useEffect(() => {
        if (storeInfo) {
            setFieldValue("storename", storeInfo?.name);
            setFieldValue("pickupaddress", storeInfo?.pickup_address);
            setFieldValue("returnaddress", storeInfo?.return_address);
            setStoreId(storeInfo?.id);
            setStoreStatus(storeInfo?.status);
        }
    }, [storeInfo]);

    useEffect(() => {
        if (pathname === "/store-list") {
            dispatch(setActiveTab("Store List"));
        }
    }, [dispatch, pathname]);

    return (
        <div className="w-full flex flex-col gap-10">
            <div className="bg-white shadow-lg rounded-lg p-4 justify-between flex flex-row gap-10">
                <div className="flex flex-col justify-between w-full gap-4">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-2xl font-bold text-text_1">Store List</div>
                        <div
                            onClick={() => {
                                if (permissions?.includes("CreateStore")) {
                                    resetForm();
                                    setShowModal(true);
                                    setStoreInfo(null);
                                } else {
                                    toast.info("You have not permitted!");
                                }
                            }}
                            className="py-2 px-3 border border-secondary text-secondary rounded bg-secondary/10 flex items-center justify-center gap-2 cursor-pointer transition-all hover:bg-secondary hover:animate-pulse hover:text-white"
                        >
                            Create New Store
                        </div>
                    </div>
                    <div className="flex flex-row gap-4 items-center w-full">
                        <input
                            type="text"
                            className="flex-grow p-2 w-full focus:ring-0 border-2 rounded-lg focus:border-secondary focus:outline-none"
                            placeholder={`Search by store name`}
                            onChange={(e) => setFilterText(e.target.value)}
                            value={filterText}
                        />
                        <button
                            disabled={!filterText}
                            onClick={() => setFilterText("")}
                            className={`py-2 w-24 text-center border border-warning text-warning rounded bg-warning/10 flex items-center justify-center ${!filterText
                                ? ""
                                : "cursor-pointer transition-all hover:bg-warning hover:animate-pulse hover:text-white"
                                }`}
                        >
                            Clear
                        </button>
                    </div>
                </div>
                <div className="h-40 hidden lg:flex">
                    <img
                        src="https://res.cloudinary.com/di23omfvx/image/upload/v1692428968/self_driving_car_Road_1_npjom3.gif"
                        alt="self drive"
                        className="h-full w-full"
                    />
                </div>
            </div>
            <div className="overflow-x-auto bg-white shadow-lg rounded-lg p-2">
                <DataTable
                    columns={columns}
                    data={filteredBranch}
                    pagination
                    persistTableHead
                    highlightOnHover
                />
            </div>

            <CustomModal
                showModal={showModal}
                setShowModal={setShowModal}
                submitButton={true}
                handleSubmit={handleSubmit}
                title={storeInfo ? "Update Store" : "Create New Store"}
            >
                <form
                    onSubmit={handleSubmit}
                    className="w-full flex flex-col gap-2 mt-5 min-w-[70%]"
                >
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="STORE NAME"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.storename}
                            touched={touched.storename}
                            error={errors.storename}
                            handleBlur={handleBlur}
                        />
                        {storeInfo && <div className="w-full mt-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                SELECT STORE STATUS
                            </div>
                            <select
                                id="status"
                                value={storeStatus}
                                onChange={(e) => setStoreStatus(e.target.value)}
                                className="w-full border-2 p-2 rounded-lg focus:outline-none focus:border-secondary"
                            >
                                <option value="Active">
                                    Active
                                </option>
                                <option value="Inactive">
                                    Inactive
                                </option>
                            </select>
                        </div>}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="PICKUP ADDRESS"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.pickupaddress}
                            touched={touched.pickupaddress}
                            error={errors.pickupaddress}
                            handleBlur={handleBlur}
                        />
                        <CustomInput
                            label="RETURN ADDRESS"
                            type="text"
                            setFieldValue={setFieldValue}
                            value={values.returnaddress}
                            touched={touched.returnaddress}
                            error={errors.returnaddress}
                            handleBlur={handleBlur}
                        />
                    </div>
                </form>
            </CustomModal>

            <CustomModal
                showModal={deleteModal}
                setShowModal={setDeleteModal}
                submitButton={true}
                handleSubmit={handleDeleteStore}
                title={'Deleting Process'}
                submitButtonText='Yes'
                closeButtonText="No"
                className="sm:min-w-[70%] md:min-w-[60%] lg:min-w-[25vw] xl:min-w-[20vw]"
                warning={true}
            >
                <div className="text-lg text-warning ">Do you want to delete this store ?</div>
            </CustomModal>

            {(storeListLoading || createResult.isLoading || updateResult.isLoading || profileLoading || deleteResult?.isLoading) && <CustomLoading />}
        </div>
    );
};

export default StoreList;
